import CockpitHeader from "../molecules/CockpitHeader";
import ResetConfirmModal from "../molecules/ResetConfirmModal";
import IneffectiveNoteModal from "../molecules/IneffectiveNoteModal";
import ScoreBoard from "../molecules/ScoreBoard";
import StatusRibbon from "../molecules/StatusRibbon";
import ExtraTimeAlert from "../molecules/ExtraTimeAlert";
import DriftBanner from "../molecules/DriftBanner";
import { MatchPeriodSelector } from "../molecules/MatchPeriodSelector";
import DuplicateTelemetryPanel from "../molecules/DuplicateTelemetryPanel";
import DuplicateHighlightBanner from "../molecules/DuplicateHighlightBanner";
import ToastNotification from "../molecules/ToastNotification";
import PreMatchLineupPanel from "./PreMatchLineupPanel";
import { XCircle } from "../../../../components/icons";
import type { Match } from "../../types";
import type {
  DuplicateHighlight,
  DuplicateStats,
} from "../../../../store/useMatchLogStore";
import type { PeriodPhase } from "../../hooks/usePeriodManager";

interface CockpitTopSectionProps {
  t: (...args: any[]) => any;
  match: Match;
  isConnected: boolean;
  queuedCount: number;
  failedCount: number;
  pendingAckCount: number;
  isSubmitting: boolean;
  isAdmin: boolean;
  openResetModal: () => void;
  resetBlocked: boolean;
  resetTooltip: string | undefined;
  undoError: string | null;
  viewMode: "logger" | "analytics" | "review";
  setViewMode: (mode: "logger" | "analytics" | "review") => void;
  statusOverride: Match["status"] | undefined;
  showResetModal: boolean;
  setShowResetModal: (show: boolean) => void;
  isCompletedOrFulltime: boolean;
  resetDisabledReason: string | undefined;
  resetConfirmText: string;
  setResetConfirmText: (value: string) => void;
  confirmGlobalReset: () => Promise<void>;
  ineffectiveNoteOpen: boolean;
  ineffectiveActionType: string;
  ineffectiveTeamSelection: "home" | "away";
  ineffectiveActionDropdownOpen: boolean;
  ineffectiveTeamDropdownOpen: boolean;
  ineffectiveNoteText: string;
  manualHomeTeamLabel: string;
  manualAwayTeamLabel: string;
  setIneffectiveActionDropdownOpen: (
    open: boolean | ((prev: boolean) => boolean),
  ) => void;
  setIneffectiveTeamDropdownOpen: (
    open: boolean | ((prev: boolean) => boolean),
  ) => void;
  setIneffectiveActionType: (value: any) => void;
  setIneffectiveTeamSelection: (value: "home" | "away") => void;
  setIneffectiveNoteText: (value: string) => void;
  cancelIneffectiveNote: () => void;
  confirmIneffectiveNote: () => void;
  liveScore: { home: number; away: number };
  transitionError: string | null;
  toast: { message: string; actionLabel?: string; action?: () => void } | null;
  currentStatusNormalized: Match["status"];
  currentPhase: PeriodPhase;
  clockMode: "EFFECTIVE" | "INEFFECTIVE";
  isGlobalClockRunning: boolean;
  cockpitLocked: boolean;
  lockReason: string | undefined;
  showExtraTimeAlert: boolean;
  extraTimeSeconds: number;
  transitionToHalftime: () => void;
  transitionToFulltime: () => void;
  dismissExtraTimeAlert: () => void;
  showDriftNudge: boolean;
  driftSeconds: number;
  fetchMatch: () => Promise<void>;
  operatorPeriod: number;
  isExtraTime: boolean;
  globalClock: string;
  guardTransition: (target: Match["status"], fn?: () => void) => void;
  transitionToSecondHalf: () => void;
  transitionToExtraFirst: () => void;
  transitionToExtraHalftime: () => void;
  transitionToExtraSecond: () => void;
  transitionToPenalties: () => void;
  finishMatch: () => void;
  transitionDisabled: boolean;
  /** PMA-COCK-200 (UC2): threaded straight to MatchPeriodSelector's own
   * penaltiesTransitionDisabled prop — see that component's doc comment. */
  penaltiesTransitionDisabled: boolean;
  transitionReason: string | undefined;
  duplicateStats: DuplicateStats;
  lastDuplicateSummaryDetails: string;
  lastDuplicateSummaryDefault: string;
  lastDuplicateTeamName: string | null;
  lastDuplicateSeenAt: string | null;
  resetDuplicateStats: () => void;
  duplicateHighlight: DuplicateHighlight | null;
  duplicateDetailsDefault: string;
  duplicateSessionDetailsSuffix: string;
  duplicateSessionSummaryDefault: string;
  duplicateExistingEventDefault: string;
  clearDuplicateHighlight: () => void;
  dismissToast: () => void;
  onShare?: () => void;
  shareCopied?: boolean;
  /** PMA-COCK-106 (3rd revision): true when the landscape-tablet rail is
   * active (`LoggerCockpit.tsx`'s `isRailLayout`) — ScoreBoard renders
   * inside that rail instead, beside the pitch, so it must not ALSO render
   * here (duplicate `data-testid="home-score"`/`"away-score"`). Default/
   * undefined = show here, unchanged for every other viewport. */
  hideScoreBoard?: boolean;
  /** PMA-COCK-210 rail relocation (EPIC-61, client wave 2026-09-19, lead
   * decision 10): true when the landscape-tablet rail is active
   * (`LoggerCockpit.tsx`'s `isRailLayout`) — the phase ladder renders
   * inside that rail instead (`LoggerView.tsx`, beside `MatchTimerDisplay`),
   * so it must not ALSO render here (duplicate `phase-ladder`/`btn-*`
   * testids). Same single-mount-either-way discipline as `hideScoreBoard`
   * immediately above. Default/undefined = show here, unchanged for every
   * other viewport (portrait, desktop, phone). */
  hideMatchPeriodSelector?: boolean;
  /** PMA-COCK-171: who is currently on the field, per team — threaded into
   * the lineup panel so its live bench-only mode can lock a substitute who
   * has already entered. */
  onFieldIds: { home: Set<string>; away: Set<string> };
}

export default function CockpitTopSection({
  t,
  match,
  isConnected,
  queuedCount,
  failedCount,
  pendingAckCount,
  isSubmitting,
  isAdmin,
  openResetModal,
  resetBlocked,
  resetTooltip,
  undoError,
  viewMode,
  setViewMode,
  statusOverride,
  showResetModal,
  setShowResetModal,
  isCompletedOrFulltime,
  resetDisabledReason,
  resetConfirmText,
  setResetConfirmText,
  confirmGlobalReset,
  ineffectiveNoteOpen,
  ineffectiveActionType,
  ineffectiveTeamSelection,
  ineffectiveActionDropdownOpen,
  ineffectiveTeamDropdownOpen,
  ineffectiveNoteText,
  manualHomeTeamLabel,
  manualAwayTeamLabel,
  setIneffectiveActionDropdownOpen,
  setIneffectiveTeamDropdownOpen,
  setIneffectiveActionType,
  setIneffectiveTeamSelection,
  setIneffectiveNoteText,
  cancelIneffectiveNote,
  confirmIneffectiveNote,
  liveScore,
  transitionError,
  toast,
  currentStatusNormalized,
  currentPhase,
  clockMode,
  isGlobalClockRunning,
  cockpitLocked,
  lockReason,
  showExtraTimeAlert,
  extraTimeSeconds,
  transitionToHalftime,
  transitionToFulltime,
  dismissExtraTimeAlert,
  showDriftNudge,
  driftSeconds,
  fetchMatch,
  operatorPeriod,
  isExtraTime,
  globalClock,
  guardTransition,
  transitionToSecondHalf,
  transitionToExtraFirst,
  transitionToExtraHalftime,
  transitionToExtraSecond,
  transitionToPenalties,
  finishMatch,
  transitionDisabled,
  penaltiesTransitionDisabled,
  transitionReason,
  duplicateStats,
  lastDuplicateSummaryDetails,
  lastDuplicateSummaryDefault,
  lastDuplicateTeamName,
  lastDuplicateSeenAt,
  resetDuplicateStats,
  duplicateHighlight,
  duplicateDetailsDefault,
  duplicateSessionDetailsSuffix,
  duplicateSessionSummaryDefault,
  duplicateExistingEventDefault,
  clearDuplicateHighlight,
  dismissToast,
  onShare,
  shareCopied,
  hideScoreBoard = false,
  hideMatchPeriodSelector = false,
  onFieldIds,
}: CockpitTopSectionProps) {
  return (
    <>
      <header className="bg-slate-900 shadow-md border-b border-slate-700/80">
        {/* PMA-COCK-106: additional gap tightening at the landscape-tablet
            rail breakpoint specifically (`max-[1180px]:landscape:`),
            layered on top of the pre-existing `md:` tier. ScoreBoard's own
            render call below is now conditional again (3rd rail-scope
            revision — the rail carries ScoreBoard+MatchTimerDisplay+
            RefereeActionBar, see LoggerView.tsx). */}
        <div className="w-full max-w-none min-w-0 px-4 sm:px-6 xl:px-8 2xl:px-10 py-3 md:py-2 max-[1180px]:landscape:py-1.5 flex flex-col gap-3 md:gap-2 max-[1180px]:landscape:gap-1.5">
          <CockpitHeader
            t={t}
            isConnected={isConnected}
            queuedCount={queuedCount}
            failedCount={failedCount}
            isSubmitting={isSubmitting}
            isAdmin={isAdmin}
            openResetModal={openResetModal}
            resetBlocked={resetBlocked}
            resetTooltip={resetTooltip}
            undoError={undoError}
            viewMode={viewMode}
            setViewMode={setViewMode}
            matchTimeSeconds={match.match_time_seconds || 0}
            statusOverride={statusOverride}
            matchStatus={match.status}
            onShare={onShare}
            shareCopied={shareCopied}
          />

          <ResetConfirmModal
            show={showResetModal}
            isCompletedOrFulltime={isCompletedOrFulltime}
            resetBlocked={resetBlocked}
            resetDisabledReason={resetDisabledReason}
            resetConfirmText={resetConfirmText}
            setResetConfirmText={setResetConfirmText}
            onCancel={() => {
              setShowResetModal(false);
              setResetConfirmText("");
            }}
            onConfirm={confirmGlobalReset}
            t={t}
          />
          <IneffectiveNoteModal
            open={ineffectiveNoteOpen}
            ineffectiveActionType={ineffectiveActionType}
            ineffectiveTeamSelection={ineffectiveTeamSelection}
            ineffectiveActionDropdownOpen={ineffectiveActionDropdownOpen}
            ineffectiveTeamDropdownOpen={ineffectiveTeamDropdownOpen}
            ineffectiveNoteText={ineffectiveNoteText}
            manualHomeTeamLabel={manualHomeTeamLabel}
            manualAwayTeamLabel={manualAwayTeamLabel}
            setIneffectiveActionDropdownOpen={setIneffectiveActionDropdownOpen}
            setIneffectiveTeamDropdownOpen={setIneffectiveTeamDropdownOpen}
            setIneffectiveActionType={setIneffectiveActionType}
            setIneffectiveTeamSelection={setIneffectiveTeamSelection}
            setIneffectiveNoteText={setIneffectiveNoteText}
            onCancel={cancelIneffectiveNote}
            onConfirm={confirmIneffectiveNote}
            t={t}
          />

          {/* ── Score + Status (single column — every component full-width) ── */}
          <div className="flex flex-col gap-3 max-[1180px]:landscape:gap-1.5 min-w-0">
            {/* Score board — full width. Suppressed when the rail is active
                (`hideScoreBoard`) — it renders inside that rail instead
                (`LoggerView.tsx`), the same single mount either way. */}
            {!hideScoreBoard && (
              <div className="w-full">
                <ScoreBoard
                  t={t}
                  match={match}
                  liveScore={liveScore}
                  queuedCount={queuedCount}
                  pendingAckCount={pendingAckCount}
                  transitionError={transitionError}
                  toastMessage={toast?.message}
                />
              </div>
            )}

            {/* Status ribbon + period selector + alerts — full width */}
            <div className="w-full flex flex-col gap-2 min-w-0">
              <StatusRibbon
                t={t}
                currentStatusNormalized={currentStatusNormalized}
                currentPhase={currentPhase}
                clockMode={clockMode}
                isGlobalClockRunning={isGlobalClockRunning}
                cockpitLocked={cockpitLocked}
                lockReason={lockReason}
              />

              {/* PMA-COCK-209 (EPIC-61, client wave 2026-09-19, brief 5/AC4 —
                  the actual mechanism behind "at minute 120, after both
                  extra times, only 'finalizar partido' appeared, and it
                  ended the match — penalties were unreachable"). This alert
                  auto-shows the instant `extraTimeSeconds > 0` — for
                  SECOND_HALF_EXTRA_TIME that is exactly 15:00 into ET2, i.e.
                  global clock 120:00, precisely the client's reported
                  moment — and its own button is literally labelled
                  `extraTime.endMatch` ("Finalizar Partido" in ES, identical
                  string to `logger.period.endMatch` used by two OTHER
                  buttons elsewhere in this same view). `onTransition` used
                  to call `transitionToHalftime`/`transitionToFulltime`
                  DIRECTLY — bypassing `guardTransition`/`isTransitionAllowed`
                  entirely (unlike every other transition control on this
                  page) — and, worse, targeted the WRONG phase for both
                  branches: FIRST_HALF_EXTRA_TIME's "Go to Halftime" called
                  `transitionToHalftime` (regular HALFTIME), when the correct
                  next phase from extra time is EXTRA_HALFTIME
                  (`transitionToExtraHalftime`, `Live_Extra_First ->
                  Extra_Halftime` is what the backend's own
                  `ALLOWED_STATUS_TRANSITIONS` permits — Halftime is not a
                  legal target from `Live_Extra_First` at all); and
                  SECOND_HALF_EXTRA_TIME's "Finalizar Partido" called
                  `transitionToFulltime` (regression back to FULLTIME,
                  regulation-ended, semantically nonsensical from inside
                  extra time), instead of the `finishMatch` its own label
                  promises. An operator who reaches for this pulsing,
                  attention-grabbing alert at minute 120 — rather than
                  scrolling to `MatchPeriodSelector`'s own `btn-start-penalties`
                  beside it — got silently routed away from PENALTIES
                  entirely, with no error (the un-guarded direct call never
                  surfaces `guardTransition`'s rejection message either).
                  Fixed: both branches now route through `guardTransition`
                  (matching every other control below) and target the
                  correct phase. This does not by itself ADD a route to
                  Penalties from here — it stops this alert from silently
                  diverting the operator away from the one that already
                  exists on `MatchPeriodSelector` (`btn-start-penalties`,
                  PMA-COCK-200). */}
              {showExtraTimeAlert &&
                (currentPhase === "FIRST_HALF_EXTRA_TIME" ||
                  currentPhase === "SECOND_HALF_EXTRA_TIME") && (
                  <ExtraTimeAlert
                    phase={currentPhase}
                    extraTimeSeconds={extraTimeSeconds}
                    onTransition={
                      currentPhase === "FIRST_HALF_EXTRA_TIME"
                        ? () =>
                            guardTransition(
                              "Extra_Halftime",
                              transitionToExtraHalftime,
                            )
                        : () => guardTransition("Completed", finishMatch)
                    }
                    onDismiss={dismissExtraTimeAlert}
                    t={t}
                  />
                )}

              <div className="flex flex-col gap-2 min-w-0 overflow-x-hidden">
                <DriftBanner
                  show={showDriftNudge}
                  driftSeconds={driftSeconds}
                  onResync={fetchMatch}
                  t={t}
                />

                {/* PMA-COCK-210 rail relocation (EPIC-61, lead decision 10):
                    suppressed when the rail is active — it renders inside
                    that rail instead (`LoggerView.tsx`, beside
                    `MatchTimerDisplay`), the same single mount either way,
                    mirroring `hideScoreBoard` immediately above in this
                    file. */}
                {!hideMatchPeriodSelector && (
                  <MatchPeriodSelector
                    match={match}
                    operatorPeriod={operatorPeriod}
                    currentPhase={currentPhase}
                    isExtraTime={isExtraTime}
                    extraTimeSeconds={extraTimeSeconds}
                    globalClock={globalClock}
                    isClockRunning={isGlobalClockRunning}
                    onTransitionToHalftime={() =>
                      guardTransition("Halftime", transitionToHalftime)
                    }
                    onTransitionToSecondHalf={() =>
                      guardTransition(
                        "Live_Second_Half",
                        transitionToSecondHalf,
                      )
                    }
                    onTransitionToFulltime={() =>
                      guardTransition("Fulltime", transitionToFulltime)
                    }
                    onTransitionToExtraFirst={() =>
                      guardTransition(
                        "Live_Extra_First",
                        transitionToExtraFirst,
                      )
                    }
                    onTransitionToExtraHalftime={() =>
                      guardTransition(
                        "Extra_Halftime",
                        transitionToExtraHalftime,
                      )
                    }
                    onTransitionToExtraSecond={() =>
                      guardTransition(
                        "Live_Extra_Second",
                        transitionToExtraSecond,
                      )
                    }
                    onTransitionToPenalties={() =>
                      guardTransition("Penalties", transitionToPenalties)
                    }
                    onFinishMatch={() =>
                      guardTransition("Completed", finishMatch)
                    }
                    transitionDisabled={transitionDisabled}
                    penaltiesTransitionDisabled={penaltiesTransitionDisabled}
                    transitionReason={transitionReason}
                    t={t}
                  />
                )}

                {transitionError && (
                  <div
                    className="flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200 shadow-sm"
                    data-testid="transition-error"
                  >
                    <XCircle size={16} className="shrink-0 text-red-400" />
                    <span>{transitionError}</span>
                  </div>
                )}

                {/* EPIC-08 Phase 4 (PMA-COCK-090): in-cockpit lineup panel —
                    full edit while Pending/Scheduled, bench-only edit while
                    live (PMA-COCK-171), hard-hidden once the roster is
                    frozen for good. PreMatchLineupPanel itself owns the
                    gate. */}
                <PreMatchLineupPanel
                  match={match}
                  t={t}
                  fetchMatch={fetchMatch}
                  currentStatusNormalized={currentStatusNormalized}
                  onFieldIds={onFieldIds}
                />
              </div>
            </div>
          </div>

          {/* DuplicateTelemetryPanel — only rendered when duplicates exist */}
          {duplicateStats.count > 0 && (
            <DuplicateTelemetryPanel
              t={t}
              duplicateStats={duplicateStats}
              lastDuplicateSummaryDetails={lastDuplicateSummaryDetails}
              lastDuplicateSummaryDefault={lastDuplicateSummaryDefault}
              lastDuplicateTeamName={lastDuplicateTeamName}
              lastDuplicateSeenAt={lastDuplicateSeenAt}
              onResetDuplicateStats={resetDuplicateStats}
            />
          )}
        </div>
      </header>

      {duplicateHighlight && (
        <DuplicateHighlightBanner
          t={t}
          duplicateHighlight={duplicateHighlight}
          duplicateStats={duplicateStats}
          duplicateDetailsDefault={duplicateDetailsDefault}
          duplicateSessionDetailsSuffix={duplicateSessionDetailsSuffix}
          duplicateSessionSummaryDefault={duplicateSessionSummaryDefault}
          duplicateExistingEventDefault={duplicateExistingEventDefault}
          lastDuplicateTeamName={lastDuplicateTeamName}
          lastDuplicateSeenAt={lastDuplicateSeenAt}
          onDismiss={clearDuplicateHighlight}
          onResetDuplicateStats={resetDuplicateStats}
        />
      )}

      <ToastNotification toast={toast} onDismiss={dismissToast} />
    </>
  );
}
